import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  useCheckCampaignDncNumbersMutation,
  useGetCampaignCompliancePreflightQuery,
  useGetAiCampaignCompliancePreflightQuery,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

const COMPLIANCE_URL = "https://retreevo.com/compliance";

interface Props {
  open: boolean;
  campaignId: string | null;
  source?: "power-dialer" | "ai";
  lines?: number;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export default function TelemarketingComplianceDialog({
  open,
  campaignId,
  source = "power-dialer",
  lines = 1,
  onOpenChange,
  onConfirm,
}: Props) {
  const [acknowledged, setAcknowledged] = useState(false);

  const { data: powerDialerPreflight, isFetching: powerDialerFetching } = useGetCampaignCompliancePreflightQuery(
    campaignId ?? "",
    { skip: !open || !campaignId || source !== "power-dialer" },
  );
  const { data: aiPreflight, isFetching: aiFetching } = useGetAiCampaignCompliancePreflightQuery(campaignId ?? "", {
    skip: !open || !campaignId || source !== "ai",
  });
  const [checkCampaignNumbers, { isLoading: queueingChecks }] = useCheckCampaignDncNumbersMutation();
  const preflight = source === "ai" ? aiPreflight : powerDialerPreflight;
  const isFetching = source === "ai" ? aiFetching : powerDialerFetching;
  const unscrubbed = preflight?.unscrubbedNumbers ?? 0;
  const onDncList = preflight?.onDncListNumbers ?? 0;
  const litigators = preflight?.litigatorNumbers ?? 0;
  const checksInProgress = preflight?.checksInProgress ?? 0;
  const checkTotal = unscrubbed * (preflight?.checkPricePerNumber ?? 0);

  const handleCheckNumbers = async () => {
    if (!campaignId) {
      return;
    }
    try {
      await checkCampaignNumbers(campaignId).unwrap();
      toast.success("Checking numbers. We will notify you when the results are in.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not start the Do Not Call check"));
    }
  };

  useEffect(() => {
    if (open) {
      setAcknowledged(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Before you start dialing</DialogTitle>
          <DialogDescription>
            Outbound telemarketing requires your organisation to be registered with the National Do Not Call Registry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <p>
            Register or confirm your registration at{" "}
            <a
              href="https://telemarketing.donotcall.gov"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              telemarketing.donotcall.gov
            </a>
            .
          </p>

          {preflight?.callerIdsWithoutCallFlow && preflight.callerIdsWithoutCallFlow.length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>Caller IDs without a call flow</AlertTitle>
              <AlertDescription>
                {preflight.callerIdsWithoutCallFlow.join(", ")} can't take calls back. Leads who call back must reach
                your business and be able to ask not to be called. Add a call flow in Telephony settings.
              </AlertDescription>
            </Alert>
          )}

          {litigators > 0 && (
            <Alert variant="destructive">
              <AlertTitle>
                {litigators} number{litigators === 1 ? "" : "s"} belong{litigators === 1 ? "s" : ""} to known litigators
              </AlertTitle>
              <AlertDescription>These numbers are skipped when the campaign dials.</AlertDescription>
            </Alert>
          )}

          {onDncList > 0 && (
            <Alert variant="destructive">
              <AlertTitle>
                {onDncList} number{onDncList === 1 ? "" : "s"} on a Do Not Call list
              </AlertTitle>
              <AlertDescription>
                These numbers are skipped when the campaign dials.{" "}
                <a href={`${COMPLIANCE_URL}#do-not-call`} target="_blank" rel="noreferrer" className="underline">
                  Learn more
                </a>
              </AlertDescription>
            </Alert>
          )}

          {source === "power-dialer" && lines > 1 && preflight && (
            <Alert>
              <AlertTitle>
                {preflight.writtenConsentNumbers ?? 0} of {preflight.totalNumbers} numbers can ring on extra lines
              </AlertTitle>
              <AlertDescription>
                Only numbers with written call consent ring alongside other lines. The rest are dialed one at a time
                or skipped, as the campaign is set up.
              </AlertDescription>
            </Alert>
          )}

          {checksInProgress > 0 && (
            <Alert>
              <AlertTitle>
                Checking {checksInProgress} number{checksInProgress === 1 ? "" : "s"}
              </AlertTitle>
              <AlertDescription>We will notify you when the Do Not Call results are in.</AlertDescription>
            </Alert>
          )}

          {unscrubbed > 0 && checksInProgress === 0 && (
            <Alert>
              <AlertTitle>
                {unscrubbed} number{unscrubbed === 1 ? "" : "s"} not checked against the Do Not Call lists
              </AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  You can still dial, but calling without consent risks fines and account suspension. Checking first
                  skips numbers on a Do Not Call list and known litigators.
                </p>
                {source === "power-dialer" && (
                  <Button size="sm" variant="outline" disabled={queueingChecks} onClick={() => void handleCheckNumbers()}>
                    Check {unscrubbed} number{unscrubbed === 1 ? "" : "s"} (${checkTotal.toFixed(2)})
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          <label className="flex items-start gap-2">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={(value) => setAcknowledged(value === true)}
              className="mt-0.5"
            />
            <span>
              I confirm my organisation is registered with the National Do Not Call Registry and has the right to
              contact these leads.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!acknowledged || isFetching} onClick={onConfirm}>
            Start dialing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
